import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getCountConfig, updateCountConfig } from '../services/api';
import { FaSave, FaPalette, FaEye, FaEyeSlash, FaUndo } from 'react-icons/fa';
import '../styles/CountConfig.css';

function CountConfig({ user }) {
  const [config, setConfig] = useState({
    title: '📊 Link Count',
    description: 'Current link statistics:',
    embed_color: '#00FF00',
    nova_label: 'Nova',
    fayne_label: 'Fayne',
    total_label: 'Total',
    downloaded_label: 'Downloaded',
    show_downloaded: true,
    show_total: true,
    footer_text: 'Use /list to see all links'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await getCountConfig();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching count config:', error);
      setMessage('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig({ ...config, [field]: value });
  };

  const handleSave = async () => {
    if (!user.isAdmin) {
      setMessage('You need admin permissions to save changes');
      return;
    }

    try {
      setSaving(true);
      setMessage('');
      await updateCountConfig(config);
      setMessage('Configuration saved successfully! ✅');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving count config:', error);
      setMessage('Failed to save configuration ❌');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig({
      title: '📊 Link Count',
      description: 'Current link statistics:',
      embed_color: '#00FF00',
      nova_label: 'Nova',
      fayne_label: 'Fayne',
      total_label: 'Total',
      downloaded_label: 'Downloaded',
      show_downloaded: true,
      show_total: true,
      footer_text: 'Use /list to see all links'
    });
  };

  if (loading) {
    return (
      <div>
        <Navbar user={user} />
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Count Command Configuration</h1>
          <p>Customize the /count command messages and embed design</p>
        </div>

        {message && (
          <div className={`alert ${message.includes('✅') ? 'alert-success' : message.includes('❌') ? 'alert-error' : 'alert-info'}`}>
            {message}
          </div>
        )}

        <div className="count-config-content">
          <div className="config-form">
            <div className="form-section">
              <h2>Embed Design</h2>

              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={config.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="📊 Link Count"
                  disabled={!user.isAdmin}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={config.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Current link statistics:"
                  disabled={!user.isAdmin}
                />
              </div>

              <div className="form-group color-picker-group">
                <label>Embed Color</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={config.embed_color}
                    onChange={(e) => handleChange('embed_color', e.target.value)}
                    disabled={!user.isAdmin}
                  />
                  <input
                    type="text"
                    value={config.embed_color}
                    onChange={(e) => handleChange('embed_color', e.target.value)}
                    placeholder="#00FF00"
                    disabled={!user.isAdmin}
                    className="color-hex"
                  />
                  <FaPalette className="color-icon" />
                </div>
              </div>

              <div className="form-group">
                <label>Footer Text</label>
                <input
                  type="text"
                  value={config.footer_text || ''}
                  onChange={(e) => handleChange('footer_text', e.target.value)}
                  placeholder="Optional footer text"
                  disabled={!user.isAdmin}
                />
              </div>
            </div>

            <div className="form-section">
              <h2>Field Labels</h2>

              <div className="form-group">
                <label>Nova Label</label>
                <input
                  type="text"
                  value={config.nova_label}
                  onChange={(e) => handleChange('nova_label', e.target.value)}
                  placeholder="Nova"
                  disabled={!user.isAdmin}
                />
              </div>

              <div className="form-group">
                <label>Fayne Label</label>
                <input
                  type="text"
                  value={config.fayne_label}
                  onChange={(e) => handleChange('fayne_label', e.target.value)}
                  placeholder="Fayne"
                  disabled={!user.isAdmin}
                />
              </div>

              <div className="form-group">
                <label>Total Label</label>
                <input
                  type="text"
                  value={config.total_label}
                  onChange={(e) => handleChange('total_label', e.target.value)}
                  placeholder="Total"
                  disabled={!user.isAdmin}
                />
              </div>

              <div className="form-group">
                <label>Downloaded Label</label>
                <input
                  type="text"
                  value={config.downloaded_label}
                  onChange={(e) => handleChange('downloaded_label', e.target.value)}
                  placeholder="Downloaded"
                  disabled={!user.isAdmin}
                />
              </div>
            </div>

            <div className="form-section">
              <h2>Visibility Options</h2>

              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={config.show_total}
                    onChange={(e) => handleChange('show_total', e.target.checked)}
                    disabled={!user.isAdmin}
                  />
                  <span className="toggle-text">
                    {config.show_total ? <FaEye /> : <FaEyeSlash />}
                    Show Total Count
                  </span>
                </label>
              </div>

              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={config.show_downloaded}
                    onChange={(e) => handleChange('show_downloaded', e.target.checked)}
                    disabled={!user.isAdmin}
                  />
                  <span className="toggle-text">
                    {config.show_downloaded ? <FaEye /> : <FaEyeSlash />}
                    Show Downloaded Count
                  </span>
                </label>
              </div>
            </div>

            {user.isAdmin && (
              <div className="form-actions">
                <button onClick={handleSave} className="save-btn" disabled={saving}>
                  <FaSave /> {saving ? 'Saving...' : 'Save Configuration'}
                </button>
                <button onClick={handleReset} className="reset-btn">
                  <FaUndo /> Reset to Defaults
                </button>
              </div>
            )}

            {!user.isAdmin && (
              <div className="alert alert-info">
                🔒 You need admin permissions to modify count command settings
              </div>
            )}
          </div>

          <div className="preview-section">
            <h2>Preview</h2>
            <div className="embed-preview" style={{ borderLeftColor: config.embed_color }}>
              <div className="embed-header">
                <h3>{config.title}</h3>
                {config.description && <p className="embed-description">{config.description}</p>}
              </div>
              <div className="embed-fields">
                <div className="embed-field inline">
                  <div className="field-name">💗 {config.nova_label}</div>
                  <div className="field-value">42 links</div>
                </div>
                <div className="embed-field inline">
                  <div className="field-name">❤️ {config.fayne_label}</div>
                  <div className="field-value">38 links</div>
                </div>
                {config.show_total && (
                  <div className="embed-field inline">
                    <div className="field-name">📝 {config.total_label}</div>
                    <div className="field-value">80 links</div>
                  </div>
                )}
                {config.show_downloaded && (
                  <div className="embed-field">
                    <div className="field-name">✅ {config.downloaded_label}</div>
                    <div className="field-value">32 / 80 (40%)</div>
                  </div>
                )}
              </div>
              {config.footer_text && (
                <div className="embed-footer">
                  <span>{config.footer_text}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default CountConfig;
